import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Helper function to detect emoji characters
const isEmoji = (char) => {
  // This covers most common emoji code points
  const emojiRegex = /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2B50}\u{2B55}\u{2934}-\u{2935}\u{3030}\u{303D}\u{3297}\u{3299}\u{FE00}-\u{FE0F}\u{1F9B0}-\u{1F9B3}]/u;
  return emojiRegex.test(char);
};

// Render emoji as OpenMoji image
const renderEmojiImage = (emoji) => {
  // Handle multi-code point emojis (like heart emoji with variation selector)
  // Create a single emoji rendering rather than separate characters
  
  // Special cases first
  if (emoji === '❤️' || emoji === '❤') return (
    <img
      src="/emoji/2764.svg"
      alt={emoji}
      title={emoji}
      style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }}
    />
  );
  if (emoji === '✌️') return (
    <img
      src="/emoji/270C.svg"
      alt={emoji}
      title={emoji}
      style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }}
    />
  );
  if (emoji === '☝️') return (
    <img
      src="/emoji/261D.svg"
      alt={emoji}
      title={emoji}
      style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }}
    />
  );
  
  // Handle different code points and formats
  const codePoints = [];
  for (const char of emoji) {
    const cp = char.codePointAt(0);
    // Skip variation selectors (FE0E-FE0F)
    if (cp < 0xFE00 || cp > 0xFE0F) {
      codePoints.push(cp.toString(16).toUpperCase());
    }
  }
  
  // Skip if we don't have any valid code points
  if (codePoints.length === 0) return emoji;
  
  const filename = codePoints.join('-');
  
  return (
    <img
      src={`/emoji/${filename}.svg`}
      alt={emoji}
      title={emoji}
      style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }}
    />
  );
};

export default function OnelinerHistory() {
  const { authToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [linkInfo, setLinkInfo] = useState({});
  const pendingFetches = useRef({});
  
  // Custom image emojis
  const customEmojis = [
    { code: ':troll:', img: '/emoji/troll.png', alt: 'troll' },
    { code: ':allthethings:', img: '/emoji/allthethings.png', alt: 'allthethings' },
    { code: ':yey:', img: '/emoji/yey.png', alt: 'yey' },
    { code: ':yuno:', img: '/emoji/yuno.png', alt: 'yuno' },
    { code: ':jackie:', img: '/emoji/jackie.png', alt: 'jackie' },
    { code: ':farnsworth:', img: '/emoji/farnsworth.png', alt: 'farnsworth' },
  ];

  // Generate regex for custom emoji codes
  const customEmojiRegex = useMemo(() => {
    return new RegExp('(' + customEmojis.map(e => e.code.replace(/([.*+?^=!:${}()|[\]\\])/g, '\\$1')).join('|') + ')', 'g');
  }, [customEmojis]);

  const fetchMessages = useCallback(async (fromId = null) => {
    if (loading || !hasMore) return;
    setLoading(true);

    try {
      const res = await fetch(`/api/oneliner?limit=20${fromId ? `&from_id=${fromId}` : ''}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      const json = await res.json();
      if (json.messages.length < 20) setHasMore(false);
      setMessages((prev) => [...prev, ...json.messages]);
    } catch (err) {
      console.error('Failed to fetch oneliners:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, authToken]);

  useEffect(() => {
    fetchMessages();
    // Only run on mount
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 10 &&
        !loading &&
        hasMore
      ) {
        const lastId = messages.length ? messages[messages.length - 1].id : null;
        fetchMessages(lastId);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, messages, fetchMessages]);

  const fetchLinkInfo = async (type, id) => {
    const key = `${type}:${id}`;
    if (linkInfo[key] || pendingFetches.current[key]) return;
    pendingFetches.current[key] = true;
    try {
      const res = await fetch(`/api/${type}/${id}`);
      const json = await res.json();
      if (type === 'artist') {
        setLinkInfo((prev) => ({ ...prev, [key]: json.songs?.[0]?.song?.artist || 'Artist' }));
      } else if (type === 'song') {
        setLinkInfo((prev) => ({ ...prev, [key]: json.song?.title || 'Song' }));
      }
    } catch {
      setLinkInfo((prev) => ({ ...prev, [key]: type === 'artist' ? 'Artist' : 'Song' }));
    }
  };

  const renderMessageWithLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const artistRegex = /^https?:\/\/hypr\.website\/artist\/(\d+)$/;
    const songRegex = /^https?:\/\/hypr\.website\/song\/(\d+)$/;
    const parts = text.split(urlRegex);

    return parts.map((part, i) => {
      if (urlRegex.test(part)) {
        let match;
        if ((match = artistRegex.exec(part))) {
          const artistId = match[1];
          const key = `artist:${artistId}`;
          if (!linkInfo[key]) fetchLinkInfo('artist', artistId);
          return (
            <Link key={i} to={`/artist/${artistId}`} className="text-blue-600 underline">
              {linkInfo[key] || `Artist #${artistId}`}
            </Link>
          );
        }
        if ((match = songRegex.exec(part))) {
          const songId = match[1];
          const key = `song:${songId}`;
          if (!linkInfo[key]) fetchLinkInfo('song', songId);
          return (
            <Link key={i} to={`/song/${songId}`} className="text-blue-600 underline">
              {linkInfo[key] || `Song #${songId}`}
            </Link>
          );
        }
        return (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
            {part}
          </a>
        );
      }

      // Process text for emojis and custom emoji codes
      let customEmojiResults = [];
      let lastCustomIndex = 0;
      let customMatch;
      
      while ((customMatch = customEmojiRegex.exec(part)) !== null) {
        if (customMatch.index > lastCustomIndex) {
          customEmojiResults.push({type: 'text', content: part.slice(lastCustomIndex, customMatch.index)});
        }
        
        const emoji = customEmojis.find(e => e.code === customMatch[0]);
        if (emoji) {
          customEmojiResults.push({
            type: 'customEmoji', 
            content: <img
              key={`custom-${i}-${customMatch.index}`}
              src={emoji.img}
              alt={emoji.alt}
              title={emoji.code}
              style={{ display: 'inline', width: '1.5em', height: '1.5em', verticalAlign: 'middle' }}
            />
          });
        } else {
          customEmojiResults.push({type: 'text', content: customMatch[0]});
        }
        lastCustomIndex = customMatch.index + customMatch[0].length;
      }
      
      if (lastCustomIndex < part.length) {
        customEmojiResults.push({type: 'text', content: part.slice(lastCustomIndex)});
      }
      
      // Then process standard emojis in each text segment
      return (
        <span key={i}>
          {customEmojiResults.map((item, j) => {
            if (item.type === 'customEmoji') return item.content;
            
            // Process standard emojis
            const chars = Array.from(item.content);
            return chars.map((char, k) => 
              isEmoji(char) ? renderEmojiImage(char) : char
            );
          })}
        </span>
      );
    });
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <h1 className="text-xl font-semibold mb-4">Oneliner History</h1>
      <div className="space-y-4">
        {messages.map((msg) => {
          const isMe = msg.message.startsWith('/me ');
          const messageBody = isMe ? msg.message.replace(/^\/me\s+/, '') : msg.message;
          return (
            <div key={msg.id} className="border-b pb-2">
              <div className="text-sm text-gray-600 flex justify-between items-center w-full">
                {!isMe && <strong className="truncate">{msg.author}</strong>}
                <span className="text-xs text-gray-500 text-right ml-4 flex-1 text-right" style={{marginLeft:!isMe?"1rem":"auto"}}>
                  {new Date(msg.timestamp * 1000).toLocaleString()}
                </span>
              </div>
              <div className="text-gray-800">
                {isMe ? (
                  <><span className="font-bold">{msg.author}</span> {renderMessageWithLinks(messageBody)}</>
                ) : (
                  renderMessageWithLinks(messageBody)
                )}
              </div>
            </div>
          );
        })}
        {loading && <p className="text-center text-gray-500">Loading…</p>}
        {!hasMore && <p className="text-center text-gray-400 mt-4">No more messages.</p>}
        {/* Add a manual "Load more" link */}
        {!loading && hasMore && (
          <div className="text-center mt-4">
            <button
              className="text-blue-600 underline px-4 py-2 rounded hover:bg-blue-50"
              onClick={() => {
                const lastId = messages.length ? messages[messages.length - 1].id : null;
                fetchMessages(lastId);
              }}
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
